import { useEffect, useRef, type CSSProperties } from 'react';

const CARD_SYMBOLS = [
  'A♠',
  'K♠',
  'Q♥',
  'J♦',
  '10♣',
  'A♥',
  'K♦',
  'Q♠',
  'J♣',
  '7♥',
  '9♠',
  '8♦',
];

interface FallingCard {
  id: number;
  left: number;
  delay: number;
  duration: number;
  rotate: number;
  rotateSpeed: number;
  rotateYDuration: number;
  scale: number;
  symbol: string;
  opacity: number;
  isRed: boolean;
}

interface CourtFigureProps {
  rank: string;
  suit: string;
  isRed: boolean;
}

function CourtFigure({
  rank,
  suit,
  isRed,
}: CourtFigureProps) {
  if (!['J', 'Q', 'K'].includes(rank)) return null;

  const suitColor = isRed ? '#c52d2d' : '#111827';
  const gold = '#d4af37';
  const blue = '#173f72';
  const red = '#9f2323';
  const skin = '#efc6a2';
  const hair = '#c49348';
  const outline = '#24170d';
  const cream = '#f5e8c8';

  const renderJack = () => (
    <>
      <path
        d="M35 25 Q50 11 65 25 L61 32 Q50 27 39 32 Z"
        fill={red}
        stroke={outline}
        strokeWidth="1.5"
      />

      <path
        d="M39 25 Q50 17 61 25"
        fill="none"
        stroke={gold}
        strokeWidth="3"
      />

      <path
        d="M61 23 Q78 9 81 15 Q71 26 62 29"
        fill={cream}
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M36 35 Q33 54 39 59 L46 51 L45 34 Z"
        fill={hair}
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M64 35 Q67 54 61 59 L54 51 L55 34 Z"
        fill={hair}
        stroke={outline}
        strokeWidth="1"
      />

      <ellipse
        cx="50"
        cy="42"
        rx="13"
        ry="15"
        fill={skin}
        stroke={outline}
        strokeWidth="1.2"
      />

      <circle cx="45" cy="41" r="1.3" fill={outline} />
      <circle cx="55" cy="41" r="1.3" fill={outline} />

      <path
        d="M50 43 L48 48 L52 48"
        fill="none"
        stroke="#9d694e"
        strokeWidth="0.9"
      />

      <path
        d="M46 51 Q50 53 54 51"
        fill="none"
        stroke="#9f4343"
        strokeWidth="1"
      />

      <path
        d="M29 59 L42 53 L58 53 L71 59 L77 94 L23 94 Z"
        fill={blue}
        stroke={outline}
        strokeWidth="1.4"
      />

      <path
        d="M32 57 L67 89 L60 95 L26 63 Z"
        fill={red}
        stroke={gold}
        strokeWidth="2"
      />

      <path
        d="M42 53 L50 64 L58 53"
        fill={cream}
        stroke={outline}
        strokeWidth="1"
      />

      <circle
        cx="50"
        cy="72"
        r="7"
        fill={gold}
        stroke={outline}
        strokeWidth="1.2"
      />

      <text
        x="50"
        y="76"
        textAnchor="middle"
        fontSize="11"
        fill={suitColor}
        fontWeight="bold"
      >
        {suit}
      </text>

      <rect
        x="72"
        y="45"
        width="3"
        height="51"
        rx="1.5"
        fill={gold}
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M73.5 42 L69 48 L73.5 53 L78 48 Z"
        fill={red}
        stroke={gold}
        strokeWidth="1"
      />
    </>
  );

  const renderQueen = () => (
    <>
      <path
        d="M34 30 L37 17 L44 26 L50 13 L56 26 L63 17 L66 30 Z"
        fill={gold}
        stroke={outline}
        strokeWidth="1.5"
      />

      <circle cx="38" cy="21" r="2" fill={red} />
      <circle cx="50" cy="17" r="2" fill={blue} />
      <circle cx="62" cy="21" r="2" fill={red} />

      <path
        d="M35 35 Q29 57 38 65 L45 54 L45 34 Z"
        fill={hair}
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M65 35 Q71 57 62 65 L55 54 L55 34 Z"
        fill={hair}
        stroke={outline}
        strokeWidth="1"
      />

      <ellipse
        cx="50"
        cy="43"
        rx="13"
        ry="15"
        fill={skin}
        stroke={outline}
        strokeWidth="1.2"
      />

      <circle cx="45" cy="42" r="1.2" fill={outline} />
      <circle cx="55" cy="42" r="1.2" fill={outline} />

      <path
        d="M50 44 L48.5 49 L51.5 49"
        fill="none"
        stroke="#9d694e"
        strokeWidth="0.9"
      />

      <path
        d="M46 52 Q50 55 54 52"
        fill="none"
        stroke="#aa3f4b"
        strokeWidth="1.2"
      />

      <path
        d="M42 57 Q50 64 58 57"
        fill="none"
        stroke={gold}
        strokeWidth="2"
      />

      <circle
        cx="50"
        cy="62"
        r="2.5"
        fill={red}
        stroke={gold}
      />

      <path
        d="M27 62 L40 54 L60 54 L73 62 L80 95 L20 95 Z"
        fill={red}
        stroke={outline}
        strokeWidth="1.4"
      />

      <path
        d="M42 56 L58 56 L64 95 L36 95 Z"
        fill={blue}
        stroke={gold}
        strokeWidth="2"
      />

      <path
        d="M29 67 Q39 73 35 90"
        fill="none"
        stroke={gold}
        strokeWidth="3"
      />

      <path
        d="M71 67 Q61 73 65 90"
        fill="none"
        stroke={gold}
        strokeWidth="3"
      />

      <text
        x="50"
        y="83"
        textAnchor="middle"
        fontSize="14"
        fill={cream}
        fontWeight="bold"
      >
        {suit}
      </text>

      <rect
        x="74"
        y="49"
        width="3"
        height="45"
        rx="1.5"
        fill={gold}
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M75.5 44 C68 51 70 57 75.5 60 C81 57 83 51 75.5 44Z"
        fill={suitColor}
        stroke={gold}
        strokeWidth="1.4"
      />
    </>
  );

  const renderKing = () => (
    <>
      <path
        d="M32 30 L35 15 L43 25 L50 11 L57 25 L65 15 L68 30 Z"
        fill={gold}
        stroke={outline}
        strokeWidth="1.6"
      />

      <circle cx="36" cy="20" r="2" fill={red} />
      <circle cx="50" cy="16" r="2" fill={blue} />
      <circle cx="64" cy="20" r="2" fill={red} />

      <path
        d="M35 35 Q32 55 40 61 L45 49 L44 34 Z"
        fill="#d6c39b"
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M65 35 Q68 55 60 61 L55 49 L56 34 Z"
        fill="#d6c39b"
        stroke={outline}
        strokeWidth="1"
      />

      <ellipse
        cx="50"
        cy="43"
        rx="13"
        ry="15"
        fill={skin}
        stroke={outline}
        strokeWidth="1.2"
      />

      <circle cx="45" cy="41" r="1.3" fill={outline} />
      <circle cx="55" cy="41" r="1.3" fill={outline} />

      <path
        d="M50 43 L48.5 48 L52 48"
        fill="none"
        stroke="#98694d"
        strokeWidth="0.9"
      />

      <path
        d="
          M39 49
          Q41 68 50 73
          Q59 68 61 49
          Q56 55 50 54
          Q44 55 39 49 Z
        "
        fill="#e4d6b7"
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M25 66 L41 55 L59 55 L75 66 L80 96 L20 96 Z"
        fill={blue}
        stroke={outline}
        strokeWidth="1.4"
      />

      <path
        d="M26 65 L40 58 L34 94 L20 95 Z"
        fill={red}
        stroke={gold}
        strokeWidth="2"
      />

      <path
        d="M74 65 L60 58 L66 94 L80 95 Z"
        fill={red}
        stroke={gold}
        strokeWidth="2"
      />

      <path
        d="M50 64 L58 73 L50 82 L42 73 Z"
        fill={gold}
        stroke={outline}
        strokeWidth="1.2"
      />

      <text
        x="50"
        y="77"
        textAnchor="middle"
        fontSize="11"
        fill={suitColor}
        fontWeight="bold"
      >
        {suit}
      </text>

      <path
        d="M72 38 L76 38 L76 85 L72 85 Z"
        fill="#e3e6e8"
        stroke={outline}
        strokeWidth="1"
      />

      <path
        d="M74 30 L78 38 L70 38 Z"
        fill="#f1f2f3"
        stroke={outline}
        strokeWidth="1"
      />

      <rect
        x="67"
        y="83"
        width="14"
        height="4"
        rx="2"
        fill={gold}
        stroke={outline}
        strokeWidth="1"
      />

      <rect
        x="72"
        y="87"
        width="4"
        height="10"
        rx="2"
        fill={red}
        stroke={outline}
        strokeWidth="1"
      />
    </>
  );

  const renderFigure = () => {
    if (rank === 'J') return renderJack();
    if (rank === 'Q') return renderQueen();
    return renderKing();
  };

  return (
    <svg
      viewBox="0 0 100 140"
      style={{
        position: 'absolute',
        inset: '8% 13%',
        width: '74%',
        height: '84%',
        overflow: 'visible',
        zIndex: 4,
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
      aria-hidden="true"
    >
      <rect
        x="8"
        y="5"
        width="84"
        height="130"
        rx="6"
        fill="none"
        stroke={gold}
        strokeOpacity="0.45"
        strokeWidth="1.2"
      />

      <g transform="translate(0 -3)">
        {renderFigure()}
      </g>

      <path
        d="M22 70 L78 70"
        stroke={gold}
        strokeWidth="1"
        strokeOpacity="0.5"
      />

      <g transform="translate(100 140) rotate(180) translate(0 -3)">
        {renderFigure()}
      </g>
    </svg>
  );
}

const cards: FallingCard[] = Array.from(
  { length: 20 },
  (_, i) => {
    const symbol = CARD_SYMBOLS[i % CARD_SYMBOLS.length];

    return {
      id: i,
      left:
        Math.floor((i / 20) * 100) +
        Math.floor(Math.random() * 4) -
        2,
      delay: (i * 0.55) % 8,
      duration: 6 + (i % 5),
      rotate: -30 + (i % 7) * 10,
      rotateSpeed:
        (i % 2 === 0 ? 1 : -1) *
        (15 + (i % 20)),
      rotateYDuration: 0.9 + (i % 5) * 0.3,
      scale: 0.5 + (i % 4) * 0.2,
      symbol,
      opacity: 0.14 + (i % 3) * 0.05,
      isRed:
        symbol.includes('♥') ||
        symbol.includes('♦'),
    };
  }
);

export default function Hero() {
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!textRef.current) return;

      textRef.current.classList.add(
        'opacity-100',
        'translate-y-0'
      );

      textRef.current.classList.remove(
        'opacity-0',
        'translate-y-8'
      );
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  const scrollToPricing = () => {
    const el = document.querySelector('#pricing');

    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background:
          'linear-gradient(160deg, #0d2010 0%, #0a1a0c 40%, #071208 100%)',
      }}
    >
      {/* 背景 */}
<div className="absolute inset-0 w-full h-full overflow-hidden">

  {/* スマホ用背景 */}
  <img
    src="/images/hero-bg-mobile.jpg"
    alt=""
    className="
      absolute
      inset-0
      w-full
      h-full
      object-cover
      object-center
      md:hidden
    "
    style={{
      opacity: 0.58,
    }}
  />

  {/* PC・タブレット用背景 */}
  <img
    src="/images/hero-bg-stone.png"
    alt=""
    className="
      absolute
      inset-0
      hidden
      md:block
      w-full
      h-full
      object-cover
      object-center
    "
    style={{
      opacity: 0.58,
    }}
  />

  {/* 文字を読みやすくする暗いレイヤー */}
  <div
    className="absolute inset-0"
    style={{
      background:
        'linear-gradient(to bottom, rgba(7,18,8,0.25) 0%, rgba(7,18,8,0.35) 48%, rgba(7,18,8,0.82) 100%)',
    }}
  />
</div>

      {/* 落下カード */}
      <div
        className="fixed inset-0 w-full h-full overflow-hidden pointer-events-none"
        style={{
          zIndex: 1,
        }}
      >
        <style>{`
          @keyframes cardFall {
            0% {
              transform:
                translate3d(0, -120px, 0)
                rotate(var(--rot-start));
              opacity: 0;
            }

            8% {
              opacity: var(--card-opacity);
            }

            90% {
              opacity: var(--card-opacity);
            }

            100% {
              transform:
                translate3d(0, 130vh, 0)
                rotate(var(--rot-end));
              opacity: 0;
            }
          }

          @keyframes cardFlip {
            0% {
              transform: rotateY(0deg);
            }

            100% {
              transform: rotateY(360deg);
            }
          }
        `}</style>

        {cards.map((card) => {
          const rank = card.symbol.slice(0, -1);
          const suit = card.symbol.slice(-1);

          const cardColor =
            card.isRed
              ? '#d43b32'
              : '#111111';

          const isCourt =
            rank === 'J' ||
            rank === 'Q' ||
            rank === 'K';

          const cardScale =
            isCourt
              ? Math.max(card.scale * 1.15, 0.95)
              : Math.max(card.scale, 0.72);

          return (
            <div
              key={card.id}
              style={
                {
                  position: 'absolute',
                  top: '-100px',
                  left: `${card.left}%`,
                  animationName: 'cardFall',
                  animationDuration: `${card.duration}s`,
                  animationDelay: `${card.delay}s`,
                  animationTimingFunction: 'linear',
                  animationIterationCount: 'infinite',
                  perspective: '900px',
                  WebkitPerspective: '900px',
                  perspectiveOrigin: '50% 50%',
                  WebkitPerspectiveOrigin: '50% 50%',
                  willChange: 'transform',

                  '--rot-start':
                    `${card.rotate}deg`,

                  '--rot-end':
                    `${
                      card.rotate +
                      card.rotateSpeed * card.duration
                    }deg`,

                  '--card-opacity':
                    card.opacity,
                } as CSSProperties
              }
            >
              <div
                style={{
                  position: 'relative',
                  width: `${58 * cardScale}px`,
                  height: `${82 * cardScale}px`,
                  transformStyle: 'preserve-3d',
                  WebkitTransformStyle: 'preserve-3d',
                  animationName: 'cardFlip',
                  animationDuration: `${card.rotateYDuration}s`,
                  animationTimingFunction: 'linear',
                  animationIterationCount: 'infinite',
                  willChange: 'transform',
                }}
              >
                {/* 表面 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,

                    background:
                      'linear-gradient(145deg, #ffffff 0%, #f5f5f5 55%, #e8e8e8 100%)',

                    borderRadius: `${6 * cardScale}px`,
                    border:
                      '1px solid rgba(0,0,0,0.18)',

                    boxShadow:
                      '0 5px 12px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.8)',

                    overflow: 'hidden',

                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',

                    transform: 'rotateY(0deg)',
                    WebkitTransform: 'rotateY(0deg)',
                  }}
                >
                  {/* 内側の枠 */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: `${3 * cardScale}px`,
                      borderRadius: `${4 * cardScale}px`,
                      border:
                        '1px solid rgba(0,0,0,0.08)',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />

                  {/* 左上 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: `${5 * cardScale}px`,
                      left: `${5 * cardScale}px`,
                      color: cardColor,
                      fontFamily: 'Georgia, serif',
                      fontWeight: 700,
                      lineHeight: 0.85,
                      textAlign: 'center',
                      zIndex: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize:
                          `${Math.max(10, 12 * cardScale)}px`,
                        lineHeight: 1,
                      }}
                    >
                      {rank}
                    </div>

                    <div
                      style={{
                        fontSize:
                          `${Math.max(9, 10 * cardScale)}px`,
                        marginTop: `${2 * cardScale}px`,
                        lineHeight: 1,
                      }}
                    >
                      {suit}
                    </div>
                  </div>

                  {/* 中央 */}
                  {isCourt ? (
                    <CourtFigure
                      rank={rank}
                      suit={suit}
                      isRed={card.isRed}
                    />
                  ) : (
                    <>
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: cardColor,
                          fontSize:
                            `${30 * cardScale}px`,
                          fontFamily:
                            'Georgia, serif',
                          textShadow:
                            '0 1px 1px rgba(0,0,0,0.12)',
                          zIndex: 5,
                        }}
                      >
                        {suit}
                      </div>

                      <div
                        style={{
                          position: 'absolute',
                          top: '28%',
                          left: '50%',
                          transform:
                            'translateX(-50%)',
                          color: cardColor,
                          fontSize:
                            `${8 * cardScale}px`,
                          opacity: 0.7,
                          zIndex: 5,
                        }}
                      >
                        {suit}
                      </div>

                      <div
                        style={{
                          position: 'absolute',
                          bottom: '28%',
                          left: '50%',
                          transform:
                            'translateX(-50%) rotate(180deg)',
                          color: cardColor,
                          fontSize:
                            `${8 * cardScale}px`,
                          opacity: 0.7,
                          zIndex: 5,
                        }}
                      >
                        {suit}
                      </div>
                    </>
                  )}

                  {/* 右下 */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: `${5 * cardScale}px`,
                      right: `${5 * cardScale}px`,
                      color: cardColor,
                      fontFamily: 'Georgia, serif',
                      fontWeight: 700,
                      lineHeight: 0.85,
                      textAlign: 'center',
                      transform: 'rotate(180deg)',
                      zIndex: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize:
                          `${Math.max(10, 12 * cardScale)}px`,
                        lineHeight: 1,
                      }}
                    >
                      {rank}
                    </div>

                    <div
                      style={{
                        fontSize:
                          `${Math.max(9, 10 * cardScale)}px`,
                        marginTop: `${2 * cardScale}px`,
                        lineHeight: 1,
                      }}
                    >
                      {suit}
                    </div>
                  </div>
                </div>

                {/* 裏面 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,

                    borderRadius: `${6 * cardScale}px`,
                    border:
                      '1px solid rgba(0,0,0,0.35)',

                    boxShadow:
                      '0 5px 12px rgba(0,0,0,0.45)',

                    overflow: 'hidden',

                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',

                    transform: 'rotateY(180deg)',
                    WebkitTransform: 'rotateY(180deg)',

                    background:
                      'linear-gradient(145deg, #591411 0%, #8b2018 50%, #46100e 100%)',
                  }}
                >
                  {/* 外側金枠 */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: `${4 * cardScale}px`,
                      border:
                        `${1.5 * cardScale}px solid #d4af37`,
                      borderRadius:
                        `${4 * cardScale}px`,
                      boxShadow:
                        'inset 0 0 4px rgba(212,175,55,0.4)',
                    }}
                  />

                  {/* 内側枠 */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: `${7 * cardScale}px`,
                      border:
                        `${1 * cardScale}px solid rgba(255,245,210,0.7)`,
                      borderRadius:
                        `${3 * cardScale}px`,
                    }}
                  />

                  {/* 裏面パターン */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: `${10 * cardScale}px`,
                      borderRadius:
                        `${2 * cardScale}px`,
                      backgroundColor: '#681713',
                      backgroundImage: `
                        linear-gradient(
                          45deg,
                          rgba(212,175,55,0.18) 25%,
                          transparent 25%,
                          transparent 75%,
                          rgba(212,175,55,0.18) 75%
                        ),
                        linear-gradient(
                          -45deg,
                          rgba(212,175,55,0.18) 25%,
                          transparent 25%,
                          transparent 75%,
                          rgba(212,175,55,0.18) 75%
                        )
                      `,
                      backgroundSize:
                        `${8 * cardScale}px ${8 * cardScale}px`,
                    }}
                  />

                  {/* 中央装飾 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '42%',
                      aspectRatio: '1',
                      transform:
                        'translate(-50%, -50%) rotate(45deg)',
                      border:
                        `${1.2 * cardScale}px solid #d4af37`,
                      borderRadius: '16%',
                      background:
                        'rgba(70,10,8,0.75)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        transform:
                          'rotate(-45deg)',
                        color: '#d4af37',
                        fontFamily:
                          'Georgia, serif',
                        fontSize:
                          `${18 * cardScale}px`,
                        lineHeight: 1,
                      }}
                    >
                      ♠
                    </div>
                  </div>

                  {/* 上下装飾 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '16%',
                      left: '50%',
                      transform:
                        'translateX(-50%)',
                      color:
                        'rgba(212,175,55,0.65)',
                      fontSize:
                        `${6 * cardScale}px`,
                    }}
                  >
                    ◆
                  </div>

                  <div
                    style={{
                      position: 'absolute',
                      bottom: '16%',
                      left: '50%',
                      transform:
                        'translateX(-50%) rotate(180deg)',
                      color:
                        'rgba(212,175,55,0.65)',
                      fontSize:
                        `${6 * cardScale}px`,
                    }}
                  >
                    ◆
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* オーバーレイ */}
      <div
        className="absolute inset-0"
        style={{
          zIndex: 3,
          background:
            'rgba(7,18,8,0.45)',
        }}
      />

      <div
        className="absolute inset-0"
        style={{
          zIndex: 3,
          background:
            'linear-gradient(to bottom, rgba(7,18,8,0.3) 0%, rgba(7,18,8,0.15) 40%, rgba(7,18,8,0.6) 100%)',
        }}
      />

      {/* コンテンツ */}
      <div
        ref={textRef}
        className="relative text-center px-4 w-full opacity-0 translate-y-8 transition-all duration-1000 ease-out"
        style={{
          zIndex: 10,
        }}
      >
        <p className="section-label mb-4 md:mb-6 text-xs tracking-[0.4em]">
          秋葉原・神田エリア ｜ 24時間営業
        </p>

        <h1 className="mb-2">
          <span className="block text-white/80 font-noto text-sm md:text-base tracking-widest mb-1">
            秋葉原・神田のポーカーレンタルスペース
          </span>

          <span
            className="text-[#d4af37] leading-none"
            style={{
              fontSize:
                'clamp(80px, 16vw, 180px)',
              fontFamily:
                '"Stick", sans-serif',
              fontStyle: 'italic',
              transform:
                'skewX(-8deg)',
              display: 'inline-block',
              letterSpacing: '0.05em',
              textShadow:
                '4px 4px 0px rgba(0,0,0,0.5)',
            }}
          >
            デュエル
          </span>
        </h1>

        <p className="text-white/80 font-noto text-lg md:text-2xl mb-3 tracking-wide font-medium">
          身内同士でポーカー、しようぜ。
        </p>

        <p className="text-white/70 font-noto text-sm md:text-base mb-2 tracking-wide max-w-xl mx-auto leading-relaxed">
          秋葉原・神田で、友人同士だけで楽しめるポーカー貸卓。
        </p>

        <p className="text-[#d4af37] font-noto text-base md:text-lg mb-8 md:mb-12 tracking-wide font-semibold max-w-xl mx-auto leading-relaxed">
          ポーカーテーブル・チップ・トランプ完備。手ぶらでご利用いただけます。
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://upnow.jp/duel"
            target="_blank"
            rel="noopener noreferrer"
            className="px-10 py-4 bg-[#d4af37] text-black font-bold text-base rounded-full hover:bg-[#e8c84a] hover:scale-105 transition-all duration-200 whitespace-nowrap cursor-pointer min-w-[220px]"
          >
            今すぐ予約（登録不要）
          </a>

          <button
            onClick={scrollToPricing}
            className="px-10 py-4 border border-white/30 text-white font-medium text-base rounded-full hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-200 whitespace-nowrap cursor-pointer min-w-[220px]"
          >
            料金を見る
          </button>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-3 gap-4 max-w-md mx-auto">
          {[
            {
              value: '3',
              unit: '卓',
              label: 'ポーカーテーブル',
            },
            {
              value: '24',
              unit: 'H',
              label: '年中無休営業',
            },
            {
              value: '5',
              unit: '分',
              label: '駅から徒歩',
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="text-center"
            >
              <div className="flex items-baseline justify-center gap-0.5">
                <span className="font-playfair font-bold text-[#d4af37] text-3xl md:text-4xl">
                  {stat.value}
                </span>

                <span className="text-[#d4af37] text-sm font-medium">
                  {stat.unit}
                </span>
              </div>

              <p className="text-white/50 text-xs mt-1 font-noto tracking-wide">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}